import { Tabs, Tab, withStyles } from '@material-ui/core';
import PickupInfoGrid from './PickupInfoGrid/PickupInfoGrid';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PickupHistoryGrid from './PickupHistoryGrid';
import PickupHistoryGridControls from './PickupHistoryGrid/GridControls';
import PickupInfoGridControls from './PickupInfoGrid/GridControls';
import { getPickupShipmentList } from '../../utils';
import { compose } from 'recompose';
import { withUserProfile } from '../../../../../../../../userProfile/userProfileContext';
import { getDataWithQueryString } from '../../../../../../../../api/componentAPIs';
import appConstants from '../../../../../../../../appConstants';
import { orderBy } from 'lodash';
import { sortShipmentsBySeq } from '../../../utils';
import { PICKUP_INFO_GRID_TABS } from './pickupInfoGridTabConstants';
import pickupInfoGridTabStyles from './pickupInfoGridTabStyles';
import GroupPickupShipmentDetailsPanel from './GroupPickupShipmentDetailsPanel';

const styles = pickupInfoGridTabStyles;

const PickupInfoGridTab = ({
  classes,
  userData,
  pickInfoStateData
}) => {
  const location = useLocation();
  const {
    isPickupHistory,
    pickupInfo,
    shipmentAPI,
    isPastPickup,
    pastPickupShipments = [],
    shipmentGridStatus,
    shouldGetShipmentsFromAPI = false,
    isGroupPickupDialog,
    groupMemberPickups
  } = pickInfoStateData;
  const [activeTab, setActiveTab] = useState(() =>
    isPickupHistory
      ? PICKUP_INFO_GRID_TABS.pickupHistoryTab
      : PICKUP_INFO_GRID_TABS.shipmentDetailsTab
  );
  const [shipmentInfo, setShipmentInfo] = useState({
    data: [],
    gridStatus: appConstants.GRID_STATUS.LOADING
  });
  const [pickupHistoryInfo, setPickupHistoryInfo] = useState({
    data: [],
    gridStatus: appConstants.GRID_STATUS.LOADING
  });

  useEffect(() => {
    loadActiveGridData(activeTab);
  }, []);

  useEffect(() => {
    // This useEffect is for setting the pickup shipments data passed from the parent component (Past Pickups Grid).
    // If we are getting the shipments from the API here (shouldGetShipmentsFromAPI is true), we don't need to set the data here.
    if (shipmentInfo?.data?.length > 0 || shouldGetShipmentsFromAPI) {
      return;
    }
    if (isPastPickup) {
      setShipmentInfo({
        data: location?.state?.pastPickupShipments,
        gridStatus: shipmentGridStatus || appConstants.GRID_STATUS.LOADING
      });
    }
  }, [location?.state?.pastPickupShipments]);

  const handleTabChange = (event, value) => {
    setActiveTab(value);
    if (value !== activeTab) {
      loadActiveGridData(value);
    }
  };

  const loadActiveGridData = (activeTab) => {
    activeTab === PICKUP_INFO_GRID_TABS.shipmentDetailsTab
      ? loadPickupShipmentData()
      : loadPickupHistoryData();
  };

  const loadPickupShipmentData = () => {
    if (shipmentInfo.data.length > 0) {
      return;
    }
    if (pickupInfo?.bills === 0) {
      return setShipmentInfo({
        data: [],
        gridStatus: appConstants.GRID_STATUS.SUCCESS
      });
    }
    if (isPastPickup && !shouldGetShipmentsFromAPI) {
      // The `shouldGetShipmentsFromAPI` flag determines whether this component needs to fetch shipment data itself or use data already provided.
      // In the Past Pickups grid, shipment details are loaded there and passed in.
      // For record lookups, however, this component must call the API directly to get shipments.
      // Because both flows use `isPastPickup = true`, omitting the API call during a record lookup would result in no shipments being displayed.

      return setShipmentInfo({
        data: location?.state?.pastPickupShipments,
        gridStatus: shipmentGridStatus || appConstants.GRID_STATUS.LOADING
      });
    }
    const pickupIdsForShipments =
      isGroupPickupDialog && groupMemberPickups?.length
        ? groupMemberPickups.map((p) => p.pickupId).filter(Boolean)
        : null;
    return getPickupShipmentList({
      terminalCode: userData.userProfile.terminal.code,
      pickupId: pickupInfo.pickupId,
      pickupIds: pickupIdsForShipments,
      groupMemberPickups,
      shipmentInfo,
      pastPickupShipments,
      shipmentAPI,
      isPastPickup,
      pickupNumber: pickupInfo.pickupNumber
    }).then((response) => {
      response.data = sortShipmentsBySeq(response.data);
      setShipmentInfo(response);
    });
  };
  const loadPickupHistoryData = (forceUpdate = false) => {
    if (!forceUpdate && pickupHistoryInfo.data.length > 0) {
      return;
    }
    setPickupHistoryInfo({
      data: [],
      gridStatus: appConstants.GRID_STATUS.LOADING
    });
    getDataWithQueryString(
      'GetPickupHistory',
      `terminalCode=${userData.userProfile.terminal.code}&pickupNumber=${pickupInfo.pickupNumber}`
    ).then(
      (res) => {
        if (res?.status >= 200 && res?.status <= 400) {
          return setPickupHistoryInfo({
            data: orderBy(res.data, 'eventDateTime', 'desc'),
            gridStatus: appConstants.GRID_STATUS.SUCCESS
          });
        }
        return handleError();
      },
      (err) => {
        return handleError();
      }
    );
  };

  const handleError = () => {
    return setPickupHistoryInfo({ data: [], gridStatus: appConstants.GRID_STATUS.ERROR });
  };

  if (isGroupPickupDialog) {
    return (
      <GroupPickupShipmentDetailsPanel
        shipmentInfo={shipmentInfo}
        pickInfoStateData={pickInfoStateData}
        loadPickupShipmentData={loadPickupShipmentData}
      />
    );
  }

  return (
    <>
      <div className={classes.tabsHeaderContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          classes={{ indicator: classes.tabsIndicator }}
          indicatorColor="primary">
          <Tab
            classes={{
              root: classes.tabRoot,
              selected: classes.tabSelected,
              labelContainer: classes.labelContainer
            }}
            label="Shipment Details"
            value={PICKUP_INFO_GRID_TABS.shipmentDetailsTab}
          />
          <Tab
            classes={{
              root: classes.tabRoot,
              selected: classes.tabSelected,
              labelContainer: classes.labelContainer
            }}
            label="Pickup History"
            value={PICKUP_INFO_GRID_TABS.pickupHistoryTab}
          />
        </Tabs>
        {activeTab === PICKUP_INFO_GRID_TABS.pickupHistoryTab ? (
          <PickupHistoryGridControls
            gridInfo={pickupHistoryInfo}
            loadPickupHistoryData={loadPickupHistoryData}
          />
        ) : (
          <PickupInfoGridControls
            gridInfo={shipmentInfo}
            loadShipmentsData={loadPickupShipmentData}
          />
        )}
      </div>

      {activeTab === PICKUP_INFO_GRID_TABS.shipmentDetailsTab ? (
        <PickupInfoGrid
          gridInfo={shipmentInfo}
          pickInfoStateData={pickInfoStateData}
        />
      ) : (
        <PickupHistoryGrid gridInfo={pickupHistoryInfo} />
      )}
    </>
  );
};

export default compose(withUserProfile, withStyles(styles))(PickupInfoGridTab);
